/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "../../components/ui/avatar";
import { getFileType } from "../../helper/function";
import VideoSquareCover from "../../video-square.jpg";
import AudioSquareCover from "../../audio-square.jpg";
import ApproveButton from "./approve.asset";
import { actions } from "../../actions";
import CreateRentalButton from "./create.rental";
import { NFTRecord } from "../../types/nft/nfts";
import { useNavigate } from "react-router-dom";
import dataProvider from "../../authentication/dataProvider";
import { Eye, Trash2, Plus, Pencil } from "lucide-react";
import CustomLoader from "../ui/custom-loader";
import { useToast } from "../ui/use-toast";
import { generateUserKey } from "../../services/sessionKey";
import ResellPrice from "./resellprice.resell";
import { web3 } from "../../web3";
import AssetCreateAuto from "./assetCreateAuto";

const NFTList = () => {
  const [nfts, setNfts] = useState<NFTRecord[]>([]);
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [assetName, setAssetName] = useState<string | null>(null);
  const navigate = useNavigate();
  const [showPopUp, setShowPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);

  useEffect(() => {
    fetchNFTs();
  }, [page, perPage]);

  const fetchNFTs = async () => {
    try {
      setIsLoading(true);
      const response = await dataProvider.getList("assets", {
        pagination: { page, perPage },
        sort: { field: "id", order: "DESC" },
        filter: {},
      });
      setNfts(response.data);
      setTotal(response.total || response.data.length);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const pageCount = Math.ceil(total / perPage);

  const handleDelete = async (id: string) => {
    try {
      const token = localStorage.getItem("token");
      const result = await generateUserKey();
      const userKey = result.userKey; // Get token from localStorage

      await fetch(`${process.env.REACT_APP_API_URL}/asset/delete/${id}`, {
        method: "DELETE",
        headers: {
          "x-auth-token": token,
          "Content-Type": "application/json",
          sessionKey: userKey,
        },
      });

      setNfts(nfts.filter((item) => item.id !== id));
      setShowPopup(false);
      toast({ description: "Asset deleted", variant: "default" });
      setDeleteId(null);
    } catch (error) {
      toast({ description: "Error deleting asset", variant: "destructive" });
    }
  };

  const USDPriceComponent = ({ record }: { record: NFTRecord }) => {
    const [usdPrice, setUsdPrice] = useState<string>("");

    useEffect(() => {
      async function getUSDPrice(): Promise<void> {
        try {
          const resellPoolInstance = await actions.getResellPoolContract();
          const chainLinkPrice: number = Number(
            await resellPoolInstance.methods.getLatestPrice().call(),
          );
          const priceInUSD =
            (record.assetPrice * record.maxSupply * chainLinkPrice) / 10 ** 8;
          setUsdPrice(priceInUSD.toFixed(2));
        } catch (error) {
          setUsdPrice("Error");
        }
      }
      if (record?.assetPrice) {
        getUSDPrice();
      }
    }, [record]);

    return <span>{usdPrice ? `$${usdPrice}` : "Loading..."}</span>;
  };

  const AssetPriceComponent = ({ record }: { record: NFTRecord }) => {
    const [price, setPrice] = useState<string>("");

    useEffect(() => {
      async function getAssetPrice(): Promise<void> {
        try {
          if (!record?.assetAddress) return;

          const contractInstance = await actions.getPropertyAssetContract(
            record.assetAddress,
          );
          if (!contractInstance) {
            throw new Error("Failed to initialize contract");
          }

          const priceResult: string = await contractInstance.methods
            .assetPrice()
            .call();
          const priceInUSDT = web3.utils.fromWei(
            priceResult.toString(),
            "ether",
          );
          setPrice(priceInUSDT);
        } catch (error) {
          setPrice("Error");
        }
      }

      getAssetPrice();
    }, [record?.assetAddress]);

    return (
      <span>
        {price ? `${parseFloat(price).toFixed(2)} USDT` : "Loading..."}
      </span>
    );
  };

  const NFTImage = ({ record }: { record: NFTRecord }) => {
    const [ext, setExt] = useState<string | false>(false);

    useEffect(() => {
      async function getExtension(): Promise<void> {
        if (!record?.image?.format) {
          const ext = await getFileType(record?.image?.compressed);
          setExt(ext);
        } else {
          setExt(record?.image?.format);
        }
      }
      getExtension();
    }, [record]);

    if (ext === "image") {
      return (
        <Avatar className="h-12 w-12">
          <AvatarImage src={record?.image?.original} alt={record?.assetName} />
          <AvatarFallback>IMG</AvatarFallback>
        </Avatar>
      );
    }
    if (ext === "audio") {
      return (
        <Avatar className="h-12 w-12">
          <AvatarImage src={AudioSquareCover} alt="Audio file" />
          <AvatarFallback>AU</AvatarFallback>
        </Avatar>
      );
    }
    if (ext === "video") {
      return (
        <Avatar className="h-12 w-12">
          <AvatarImage src={VideoSquareCover} alt="Video file" />
          <AvatarFallback>VID</AvatarFallback>
        </Avatar>
      );
    }
    // return <Loader />;
    return null;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center min-h-[400px]">
          <CustomLoader size="lg" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full mx-auto border-none ">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>List of Assets</CardTitle>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => navigate("/assets/create")}
            className="flex items-center gap-2 bg-gradientButton  text-white   transition-colors duration-200 font-medium shadow-sm hover:bg-gradientButton hover:text-white"
            variant="outline"
          >
            <Plus className="h-4 w-4" />
            Create Asset
          </Button>
          <AssetCreateAuto onRefresh={fetchNFTs} />
        </div>
      </CardHeader>
      <CardContent>
        {showPopUp && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-sm w-full">
              <h3 className="text-lg font-semibold mb-4">
                Delete Asset- {assetName}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Are you sure you want to delete this asset?
              </p>
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPopup(false);
                    setDeleteId(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => deleteId && handleDelete(deleteId)}
                >
                  Confirm
                </Button>
              </div>
            </div>
          </div>
        )}
        <div className="rounded-md border 3xl:w-full">
          <div className="3xl:overflow-visible">
            <Table className="3xl:min-w-full">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px] text-center">
                    Asset Name
                  </TableHead>
                  <TableHead className="text-center">Image</TableHead>
                  <TableHead className="text-center">
                    Total Asset Tokens
                  </TableHead>
                  <TableHead className="text-center">
                    Asset Price Per Token(USDT)
                  </TableHead>
                  <TableHead className="text-center">
                    Total Asset Price(USD)
                  </TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">View</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                  <TableHead className="text-center">Set Property</TableHead>
                  <TableHead className="text-center">Create Rental</TableHead>
                  <TableHead className="text-center ">
                    Configure Prices
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {nfts.map((nft) => (
                  <TableRow key={nft?._id}>
                    <TableCell className="max-w-[200px] truncate py-2 text-center">
                      {nft?.assetName}
                    </TableCell>
                    <TableCell className="py-2 text-center">
                      <NFTImage record={nft} />
                    </TableCell>
                    <TableCell className="py-2 text-center">
                      {nft?.maxSupply}
                    </TableCell>
                    <TableCell className="py-2 text-center">
                      <AssetPriceComponent record={nft} />
                    </TableCell>
                    <TableCell className="py-2 text-center">
                      <USDPriceComponent record={nft} />
                    </TableCell>
                    <TableCell className="py-2 text-center">
                      {nft?.status}
                    </TableCell>
                    <TableCell className="py-2 text-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/assets/${nft?._id}`)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                    <TableCell className="py-2 text-center">
                      <div className="flex gap-3">
                        <Button
                          // variant='destructive'
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            setShowPopup(true);
                            setDeleteId(nft?._id);
                            setAssetName(nft?.assetName);
                          }}
                          style={{ background: "#7f1d1d" }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => navigate(`/assets/${nft?._id}/edit`)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="py-2 text-center">
                      <ApproveButton record={nft} />
                    </TableCell>
                    <TableCell className="py-2 text-center">
                      <CreateRentalButton record={nft} />
                    </TableCell>
                    <TableCell className="py-2 text-center">
                      <ResellPrice record={nft} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        <div className="flex items-center justify-between space-x-2 py-4 mt-4">
          <div className="flex-1 text-sm text-muted-foreground">
            {total > 0 ? (
              <>
                Showing{" "}
                <span className="font-medium">
                  {total > 0 ? Math.min((page - 1) * perPage + 1, total) : 0}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(page * perPage, total)}
                </span>{" "}
                of <span className="font-medium">{total}</span> assets
              </>
            ) : (
              "No assets found"
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <div className="flex items-center justify-center text-sm font-medium">
              Page {page} of {pageCount || 1}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => (p < pageCount ? p + 1 : p))}
              disabled={page >= pageCount || total <= page * perPage}
            >
              Next
            </Button>
            <select
              className="h-8 w-[70px] rounded-md border border-input bg-background px-2"
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1); // Reset to first page when changing items per page
              }}
            >
              {[10, 20, 30].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default NFTList;
